import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserContext } from '../contexts/UserContext';
import { useTranslation } from '../i18n/useTranslation';
import flashbookApi from '../services/flashbookApi';
import { queryKeys } from '../lib/queryClient';
import styles from './FlashBookPage.module.css';

/**
 * FlashBookPage - 플래시북 메인 페이지
 * 내 플렁슈 목록 표시 및 추가
 */
const FlashBookPage = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    // eslint-disable-next-line no-unused-vars
    const { userInfo } = useContext(UserContext);
    const { t, lang } = useTranslation();

    // 검색 관련 state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchType, setSearchType] = useState('flash'); // 'planche' | 'flash'
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [isDownloadingAll, setIsDownloadingAll] = useState(false);

    // PWA 노치 색상을 페이지 배경색(#f8f8f8)과 통일
    useEffect(() => {
        const themeColorMeta = document.querySelector('meta[name="theme-color"]');
        if (themeColorMeta) {
            themeColorMeta.setAttribute('content', '#fffdf8');
        }
    }, []);

    // 플렁슈 목록 조회 (React Query)
    const {
        data,
        isLoading,
        error,
        refetch
    } = useQuery({
        queryKey: queryKeys.flashbook.planches.all(),
        queryFn: async () => {
            const result = await flashbookApi.getPlanches();
            return result.planches || [];
        },
        // 캐시가 stale 상태면 마운트 시 리페치
        staleTime: 0,
        refetchOnMount: true,
    });

    // 통합 검색 API 사용 (백엔드 서버 측 검색)
    const { data: searchData, isFetching: isSearchLoading } = useQuery({
        queryKey: ['flashbook', 'search', searchQuery, searchType],
        queryFn: async () => {
            const result = await flashbookApi.search(searchQuery.trim(), searchType);
            return result;
        },
        staleTime: 30000, // 30초 캐시
        enabled: isSearchActive && searchQuery.trim().length > 0,
    });

    // 플렁슈 생성 mutation
    const createPlancheMutation = useMutation({
        mutationFn: (newPlanche) => flashbookApi.createPlanche(newPlanche),
        onSuccess: (result) => {
            // 캐시 무효화하여 목록 자동 갱신
            queryClient.invalidateQueries({
                queryKey: queryKeys.flashbook.planches.all()
            });

            // 생성 후 편집 페이지로 이동
            if (result.planche) {
                navigate(`/flashbook/planche/${result.planche.id}`);
            }
        },
        onError: (err) => {
            console.error('Failed to create planche:', err);
            alert(t('flashbook.createError') || '플렁슈 생성에 실패했습니다.');
        }
    });

    // 새 플렁슈 생성
    const handleAddPlanche = () => {
        createPlancheMutation.mutate({
            layout_data: [],
            planche_settings: {
                deactivation_style: 'dim'
            }
        });
    };

    // 전체 플래시 원본 이미지 ZIP 다운로드
    const handleDownloadAllFlashes = async () => {
        setIsDownloadingAll(true);

        try {
            await flashbookApi.downloadAllFlashes();
        } catch (err) {
            console.error('Failed to download all flashes:', err);
            alert(err.message || t('flashbook.downloadAllError') || '플래시 전체 다운로드에 실패했습니다.');
        } finally {
            setIsDownloadingAll(false);
        }
    };

    // 플렁슈 클릭 → 편집 페이지로
    const handlePlancheClick = (planche) => {
        navigate(`/flashbook/planche/${planche.id}`);
    };

    // 검색 실행
    const handleSearch = () => {
        if (!searchQuery.trim()) {
            setIsSearchActive(false);
            return;
        }
        setIsSearchActive(true);
    };

    // 검색 초기화
    const handleReset = () => {
        setSearchQuery('');
        setIsSearchActive(false);
    };

    // Enter 키 검색
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // 플렁슈 목록 (검색 시 서버 결과 사용)
    const filteredPlanches = isSearchActive && searchType === 'planche'
        ? (searchData?.results || [])
        : (data || []);

    // 플래시 검색 결과 (서버에서 필터링된 결과)
    const flashSearchResults = isSearchActive && searchType === 'flash'
        ? (searchData?.results || [])
        : [];

    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.error}>
                    <p>Failed to load planches</p>
                    <button onClick={() => refetch()}>Retry</button>
                </div>
            </div>
        );
    }

    const planches = filteredPlanches;

    return (
        <div className={styles.container}>
            {/* 헤더 */}
            <div className={styles.header}>
                <h1 className={styles.title}>{t('flashbook.title') || 'FLASH BOOK'}</h1>
                <div className={styles.headerActions}>
                    <button
                        className={styles.addButton}
                        onClick={handleDownloadAllFlashes}
                        disabled={isDownloadingAll}
                        title={t('flashbook.downloadAll') || 'Download all flashes'}
                        aria-label={t('flashbook.downloadAll') || 'Download all flashes'}
                    >
                        {isDownloadingAll ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" opacity="0.25" />
                                <path d="M12 2 A10 10 0 0 1 22 12" strokeLinecap="round">
                                    <animateTransform
                                        attributeName="transform"
                                        attributeType="XML"
                                        type="rotate"
                                        from="0 12 12"
                                        to="360 12 12"
                                        dur="1s"
                                        repeatCount="indefinite"
                                    />
                                </path>
                            </svg>
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <path d="M7 10l5 5 5-5" />
                                <path d="M12 15V3" />
                            </svg>
                        )}
                    </button>
                    <button
                        className={styles.addButton}
                        onClick={handleAddPlanche}
                        disabled={createPlancheMutation.isPending}
                        title={t('flashbook.newPlanche') || 'New planche'}
                        aria-label={t('flashbook.newPlanche') || 'New planche'}
                    >
                        {createPlancheMutation.isPending ? (
                            <>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" opacity="0.25" />
                                    <path d="M12 2 A10 10 0 0 1 22 12" strokeLinecap="round">
                                        <animateTransform
                                            attributeName="transform"
                                            attributeType="XML"
                                            type="rotate"
                                            from="0 12 12"
                                            to="360 12 12"
                                            dur="1s"
                                            repeatCount="indefinite"
                                        />
                                    </path>
                                </svg>
                                Creating...
                            </>
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            {/* 검색바 */}
            <div className={styles.searchBar}>
                <div className={styles.searchTypeWrapper}>
                    <select
                        className={styles.searchTypeSelect}
                        value={searchType}
                        onChange={(e) => {
                            setSearchType(e.target.value);
                            setIsSearchActive(false);
                        }}
                    >
                        <option value="planche">PLANCHE</option>
                        <option value="flash">FLASH</option>
                    </select>
                    <svg className={styles.searchTypeArrow} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 9l6 6 6-6" />
                    </svg>
                </div>
                <div className={styles.searchInputGroup}>
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder={searchType === 'planche' ? '#' : 'N.'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    {isSearchActive ? (
                        <button className={styles.searchIconButton} onClick={handleReset}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                        </button>
                    ) : (
                        <button className={styles.searchIconButton} onClick={handleSearch}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8" />
                                <path d="M21 21l-4.35-4.35" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {/* 플렁슈 목록 (플래시 검색 중에는 숨김) */}
            {!(isSearchActive && searchType === 'flash') && (planches.length === 0 ? (
                <div className={styles.empty}>
                    <p>{t('flashbook.empty') || 'No planches yet'}</p>
                    <p className={styles.emptyHint}>
                        {t('flashbook.emptyHint') || 'Click "New Planche" to create your first flash sheet'}
                    </p>
                </div>
            ) : (
                <div className={styles.grid}>
                    {planches.map((planche) => (
                        <div
                            key={planche.id}
                            className={styles.card}
                            onClick={() => handlePlancheClick(planche)}
                        >
                            <div className={styles.cardPreview}>
                                {planche.thumbnail_url ? (
                                    <img
                                        src={planche.thumbnail_url}
                                        alt={`PLANCHE ${planche.planche_number}`}
                                        className={styles.cardThumbnail}
                                        onError={(e) => {
                                            // 최대 3회 재시도
                                            const retryCount = parseInt(e.target.dataset.retry || '0');
                                            if (retryCount < 3) {
                                                e.target.dataset.retry = retryCount + 1;
                                                // 캐시 무효화를 위한 타임스탬프 추가
                                                const baseUrl = planche.thumbnail_url.split('?')[0];
                                                e.target.src = `${baseUrl}?retry=${Date.now()}`;
                                            }
                                        }}
                                    />
                                ) : (
                                    <span className={styles.cardNumber}>PLANCHE {planche.planche_number}</span>
                                )}
                            </div>
                            <div className={styles.cardInfo}>
                                <span className={styles.cardDate}>
                                    PLANCHE {planche.planche_number}
                                    {Number(planche.sold_count) > 0 && ` · SOLD ${planche.sold_count}`}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            ))}

            {/* 플래시 검색 결과 */}
            {isSearchActive && searchType === 'flash' && (
                <div className={styles.flashResultsSection}>
                    <div className={styles.flashResultsHeader}>
                        {lang === 'ko' ? '플래시 검색 결과' : lang === 'fr' ? 'Résultats Flash' : 'Flash Results'}
                        <span className={styles.flashResultsCount}>({flashSearchResults.length})</span>
                    </div>
                    {isSearchLoading ? (
                        <div className={styles.flashResultsEmpty}>
                            {lang === 'ko' ? '검색 중...' : lang === 'fr' ? 'Recherche...' : 'Searching...'}
                        </div>
                    ) : flashSearchResults.length === 0 ? (
                        <div className={styles.flashResultsEmpty}>
                            {lang === 'ko' ? '결과 없음' : lang === 'fr' ? 'Aucun résultat' : 'No results'}
                        </div>
                    ) : (
                        <div className={styles.flashResultsList}>
                            {flashSearchResults.map((flash) => {
                                // 해당 플래시가 속한 플렁슈 찾기
                                const parentPlanche = (data || []).find(p => p.id === flash.planche_id);
                                return (
                                    <div
                                        key={flash.id}
                                        className={styles.flashResultItem}
                                        onClick={() => navigate(`/flashbook/planche/${flash.planche_id}?flashId=${flash.id}`)}
                                    >
                                        <div className={styles.flashResultThumbnail}>
                                            {flash.image_url ? (
                                                <img
                                                    src={flash.image_url}
                                                    alt={flash.flash_number}
                                                />
                                            ) : (
                                                <span>{flash.flash_number}</span>
                                            )}
                                        </div>
                                        <div className={styles.flashResultInfo}>
                                            <span className={styles.flashResultNumber}>{flash.flash_number}</span>
                                            <span className={styles.flashResultPlanche}>
                                                PLANCHE {flash.planche_number || parentPlanche?.planche_number || '?'}
                                            </span>
                                        </div>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2">
                                            <path d="M9 18l6-6-6-6" />
                                        </svg>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FlashBookPage;
